import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, InputNumber, Modal, Select, Table, Tag, message } from "antd";
import DashboardHeader from "./components/DashboardHeader";
import DashboardTabbar from "./components/DashboardTabbar";
import api from "../redux/slice/apiAuth";
import { fetchMe } from "../redux/slice/authSlice";

// Shown for guidance only — the backend enforces the real minimum and balance.
const MIN_WITHDRAWAL_POINTS = 1000;
const POINTS_PER_RUPEE = 10;
const PAGE_SIZE = 5;

const STATUS_COLORS = {
  pending: "gold",
  approved: "blue",
  processing: "geekblue",
  completed: "green",
  rejected: "red",
  cancelled: "default",
};

const PAYMENT_METHODS = [
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "paypal", label: "PayPal" },
];

const methodLabel = (value) =>
  PAYMENT_METHODS.find((m) => m.value === value)?.label || value || "-";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");

// One paginated list of the user's withdrawals, optionally limited to a status.
const useWithdrawals = (status) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (targetPage) => {
      setLoading(true);
      try {
        const params = { page: targetPage, limit: PAGE_SIZE };
        if (status) params.status = status;
        const { data } = await api.get("/withdrawals", { params });
        setRows(Array.isArray(data?.data) ? data.data : []);
        setTotal(data?.pagination?.total || 0);
        setPage(targetPage);
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to load withdrawals");
      } finally {
        setLoading(false);
      }
    },
    [status]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  return { rows, total, page, loading, load };
};

const Reward = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { personalDetail } = useSelector(({ auth }) => auth);
  const balance = personalDetail?.balance ?? 0;

  const requests = useWithdrawals();
  const payments = useWithdrawals("completed");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const paymentMethod = Form.useWatch("paymentMethod", form);
  const amount = Form.useWatch("amount", form);

  useEffect(() => {
    dispatch(fetchMe({}));
  }, [dispatch]);

  const closeModal = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const onFinish = async (values) => {
    const { amount: points, paymentMethod: method, ...paymentDetails } = values;
    setSubmitting(true);
    try {
      await api.post("/withdrawals", {
        amount: points,
        currency: "INR",
        paymentMethod: method,
        paymentDetails,
      });
      message.success("Withdrawal request sent");
      closeModal();
      requests.load(1);
      dispatch(fetchMe({}));
    } catch (error) {
      message.error(error.response?.data?.message || "Could not send the withdrawal request");
    } finally {
      setSubmitting(false);
    }
  };

  const requestColumns = [
    { title: "Requested on", dataIndex: "createdAt", key: "createdAt", render: formatDate },
    { title: "Amount (WSP)", dataIndex: "amount", key: "amount" },
    { title: "Method", dataIndex: "paymentMethod", key: "paymentMethod", render: methodLabel },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"} style={{ textTransform: "capitalize" }}>
          {status}
        </Tag>
      ),
    },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", render: (text) => text || "-" },
  ];

  const paymentColumns = [
    {
      title: "Paid on",
      key: "processedAt",
      render: (_, record) => formatDate(record.processedAt || record.updatedAt),
    },
    { title: "Amount (WSP)", dataIndex: "netAmount", key: "netAmount" },
    { title: "Method", dataIndex: "paymentMethod", key: "paymentMethod", render: methodLabel },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "transactionId",
      render: (text) => text || "-",
    },
  ];

  const tablePagination = (list) => ({
    current: list.page,
    pageSize: PAGE_SIZE,
    total: list.total,
    showSizeChanger: false,
    hideOnSinglePage: true,
    onChange: (nextPage) => list.load(nextPage),
  });

  return (
    <div>
      <DashboardHeader />

      <div className="container mt-4 min-h">
        <DashboardTabbar />

        <div className="card p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <p className="text-muted mb-1">Available balance</p>
              <h3 className="mb-0 fw-bold">
                <span className="color1">{balance}</span> <span className="wsp">WSP</span>
              </h3>
              <p className="text-muted small mb-0 mt-1">
                Minimum withdrawal is {MIN_WITHDRAWAL_POINTS} WSP ({POINTS_PER_RUPEE} WSP = ₹1).
              </p>
            </div>
            <Button type="primary" size="large" onClick={() => setIsModalOpen(true)}>
              Request Withdrawal
            </Button>
          </div>
        </div>

        <div className="card p-4 mt-4">
          <h5 className="fw-bold mb-3">Withdrawal Requests</h5>
          <Table
            columns={requestColumns}
            dataSource={requests.rows}
            loading={requests.loading}
            rowKey="_id"
            pagination={tablePagination(requests)}
            locale={{ emptyText: "You have not made any withdrawal requests yet" }}
            scroll={{ x: true }}
          />
        </div>

        <div className="card p-4 mt-4">
          <h5 className="fw-bold mb-3">Received Payments</h5>
          <Table
            columns={paymentColumns}
            dataSource={payments.rows}
            loading={payments.loading}
            rowKey="_id"
            pagination={tablePagination(payments)}
            locale={{ emptyText: "No payments received yet" }}
            scroll={{ x: true }}
          />
        </div>
      </div>

      <Modal
        title="Request Withdrawal"
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        centered
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
          <Form.Item
            name="amount"
            label="Amount (WSP)"
            extra={amount ? `You will receive about ₹${(amount / POINTS_PER_RUPEE).toFixed(2)}` : `Available: ${balance} WSP`}
            rules={[
              { required: true, message: "Please enter an amount" },
              {
                type: "number",
                min: MIN_WITHDRAWAL_POINTS,
                message: `Minimum withdrawal is ${MIN_WITHDRAWAL_POINTS} WSP`,
              },
              {
                validator: (_, value) =>
                  !value || value <= balance
                    ? Promise.resolve()
                    : Promise.reject(new Error("Amount is more than your available balance")),
              },
            ]}
          >
            <InputNumber size="large" style={{ width: "100%" }} precision={0} placeholder="e.g. 1000" />
          </Form.Item>

          <Form.Item
            name="paymentMethod"
            label="Payment method"
            rules={[{ required: true, message: "Please select a payment method" }]}
          >
            <Select size="large" placeholder="Select payment method" options={PAYMENT_METHODS} />
          </Form.Item>

          {paymentMethod === "upi" && (
            <Form.Item
              name="upiId"
              label="UPI ID"
              rules={[
                { required: true, message: "Please enter your UPI ID" },
                { pattern: /^[\w.-]{2,}@[a-zA-Z]{2,}$/, message: "Enter a valid UPI ID, e.g. name@bank" },
              ]}
            >
              <Input size="large" placeholder="name@bank" />
            </Form.Item>
          )}

          {paymentMethod === "paypal" && (
            <Form.Item
              name="paypalEmail"
              label="PayPal email"
              rules={[
                { required: true, message: "Please enter your PayPal email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input size="large" placeholder="you@example.com" />
            </Form.Item>
          )}

          {paymentMethod === "bank_transfer" && (
            <>
              <Form.Item
                name="accountName"
                label="Account holder name"
                rules={[{ required: true, whitespace: true, message: "Please enter the account holder name" }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name="accountNumber"
                label="Account number"
                rules={[
                  { required: true, message: "Please enter the account number" },
                  { pattern: /^\d{6,20}$/, message: "Account number should be 6 to 20 digits" },
                ]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name="bankName"
                label="Bank name"
                rules={[{ required: true, whitespace: true, message: "Please enter the bank name" }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name="ifscCode"
                label="IFSC code"
                rules={[{ required: true, whitespace: true, message: "Please enter the IFSC code" }]}
              >
                <Input size="large" />
              </Form.Item>
            </>
          )}

          <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
            Send Request
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default Reward;
